// ── 점수 관리 훅 — 연/월 상태, 목록 로드, 수동 항목 편집·저장 ────────────────
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import { nowKst } from '../../tm/tmsummary/utils';

/** 기본 조회월 = 전월 (그 달 실적이 마감된 뒤 화합·기타를 입력하는 흐름) */
function defaultYearMonth() {
  const n = nowKst();
  const y = n.getFullYear();
  const m = n.getMonth() + 1;
  return m === 1 ? { year: y - 1, month: 12 } : { year: y, month: m - 1 };
}

export function useScoreMonth({ companyCode, showToast }) {
  const init = defaultYearMonth();
  const [year, setYear] = useState(init.year);
  const [month, setMonth] = useState(init.month);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  // 편집 중인 수동 점수: { [userId]: { [itemKey]: '문자열' } }
  const [drafts, setDrafts] = useState({});

  // showToast 는 호출자가 매 렌더마다 새로 만들 수 있다.
  //   deps 에 넣으면 load → setState → 재렌더 → 새 showToast → load … 무한 반복이 된다.
  //   ref 로 최신 함수만 들고 있고 deps 에서는 제외한다.
  const toastRef = useRef(showToast);
  toastRef.current = showToast;

  const load = useCallback(async () => {
    setLoading(true);
    setDrafts({});
    const params = new URLSearchParams({ company_code: companyCode, year: String(year), month: String(month) });
    const res = await apiClient.get(`/api/admin/tm-score/list?${params.toString()}`);
    setLoading(false);
    if (!res.ok) {
      setData(null);
      toastRef.current?.(res.data?.message || '점수 목록을 불러오지 못했습니다.', 'error');
      return;
    }
    setData(res.data);
  }, [companyCode, year, month]);

  useEffect(() => { load(); }, [load]);

  const manualKeys = useMemo(
    () => (data?.rule?.config?.items || []).filter((i) => i.mode === 'manual'),
    [data]
  );

  /** 현재 화면 값(초안 우선) */
  const valueOf = useCallback((row, itemKey) => {
    const draft = drafts[row.userId]?.[itemKey];
    if (draft !== undefined) return draft;
    const item = row.items.find((i) => i.key === itemKey);
    return item ? String(item.score) : '';
  }, [drafts]);

  /** 저장된 값(=사람이 입력한 값)인지 */
  const isManualSet = useCallback((row, itemKey) => {
    const item = row.items.find((i) => i.key === itemKey);
    return item?.pointSource === 'manual';
  }, []);

  function patchDraft(userId, itemKey, raw) {
    // 숫자·소수점만 허용 (spinner 금지 규칙에 따라 type="text" 사용)
    const value = String(raw).replace(/[^0-9.]/g, '').slice(0, 5);
    setDrafts((prev) => ({ ...prev, [userId]: { ...(prev[userId] || {}), [itemKey]: value } }));
  }

  /** 저장 대상: 초안이 있고 현재 점수와 다른 항목만 */
  const pendingByUser = useMemo(() => {
    const out = [];
    Object.entries(drafts).forEach(([userIdText, items]) => {
      const userId = Number(userIdText);
      const row = (data?.rows || []).find((r) => r.userId === userId);
      if (!row) return;
      const changed = [];
      Object.entries(items).forEach(([itemKey, raw]) => {
        const cur = row.items.find((i) => i.key === itemKey);
        if (!cur) return;
        if (String(raw).trim() === '') { // 빈 값 = 기본값으로 되돌림
          if (cur.pointSource === 'manual') changed.push({ itemKey, points: null });
          return;
        }
        if (Number(raw) !== Number(cur.score)) changed.push({ itemKey, points: Number(raw) });
      });
      if (changed.length) out.push({ userId, name: row.name, items: changed });
    });
    return out;
  }, [drafts, data]);

  const pendingCount = pendingByUser.reduce((s, u) => s + u.items.length, 0);

  /** 범위 검증 — 저장 버튼 활성 조건 */
  const invalidCount = useMemo(() => {
    let n = 0;
    Object.entries(drafts).forEach(([userIdText, items]) => {
      Object.entries(items).forEach(([itemKey, raw]) => {
        if (String(raw).trim() === '') return;
        const def = manualKeys.find((k) => k.key === itemKey);
        const v = Number(raw);
        if (!def) return;
        if (!Number.isFinite(v) || v < Number(def.minPoints || 0) || v > Number(def.maxPoints)) n += 1;
      });
    });
    return n;
  }, [drafts, manualKeys]);

  async function save() {
    if (pendingByUser.length === 0 || invalidCount > 0) return;
    setSaving(true);
    let failed = 0;
    for (const entry of pendingByUser) {
      // eslint-disable-next-line no-await-in-loop
      const res = await apiClient.put('/api/admin/tm-score/manual', {
        company_code: companyCode, year, month, user_id: entry.userId, items: entry.items,
      });
      if (!res.ok) { failed += 1; toastRef.current?.(`${entry.name}: ${res.data?.message || '저장 실패'}`, 'error'); }
    }
    setSaving(false);
    if (failed === 0) toastRef.current?.(`${pendingByUser.length}명의 점수를 저장했습니다.`, 'success');
    await load();
  }

  function moveMonth(delta) {
    let y = year;
    let m = month + delta;
    if (m < 1) { y -= 1; m = 12; }
    if (m > 12) { y += 1; m = 1; }
    setYear(y);
    setMonth(m);
  }

  return {
    year, month, setYear, setMonth, moveMonth,
    loading, saving, data, manualKeys,
    valueOf, isManualSet, patchDraft,
    pendingCount, invalidCount, save, reload: load,
  };
}
